import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Shield, Clock, Users, Award, Star } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { SharedNav, SharedFooter, Reveal } from '../components/SharedLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutPage = () => {
  const { lang } = useLanguage();
  useEffect(() => { axios.post(`${API}/track`, { event_type: 'page_view', page: '/about' }).catch(() => {}); }, []);

  const milestones = [
    { year: '1994', en: 'Lone Star Bonding Co. founded in Houston, TX — the first Latino-owned bail bond company in the city.', es: 'Lone Star Bonding Co. fundada en Houston, TX — la primera empresa de fianzas latina de la ciudad.' },
    { year: '2000', en: 'Expanded operations to serve all Harris County criminal courts.', es: 'Operaciones expandidas para servir todos los tribunales criminales del Condado de Harris.' },
    { year: '2010', en: 'Recognized as a top bail bond agency in the Greater Houston area.', es: 'Reconocida como una de las principales agencias de fianzas del area de Houston.' },
    { year: '2018', en: 'Extended coverage to Montgomery County. Thousands of families served.', es: 'Cobertura extendida al Condado de Montgomery. Miles de familias atendidas.' },
    { year: '2024', en: 'Launched digital intake platform — same trusted service, now available online 24/7.', es: 'Lanzamiento de plataforma digital — el mismo servicio de confianza, ahora disponible en linea las 24 horas.' },
  ];

  const values = [
    { icon: <Shield size={24} />, title: lang === 'es' ? 'Integridad' : 'Integrity', desc: lang === 'es' ? 'Cada decision se toma con honestidad y respeto.' : 'Every decision is made with honesty and respect.' },
    { icon: <Clock size={24} />, title: lang === 'es' ? 'Urgencia' : 'Urgency', desc: lang === 'es' ? 'Entendemos que cada minuto cuenta para su familia.' : 'We understand every minute counts for your family.' },
    { icon: <Users size={24} />, title: lang === 'es' ? 'Comunidad' : 'Community', desc: lang === 'es' ? 'Houston es nuestro hogar. Proteger a nuestra comunidad es nuestra mision.' : 'Houston is our home. Protecting our community is our mission.' },
    { icon: <Award size={24} />, title: lang === 'es' ? 'Experiencia' : 'Experience', desc: lang === 'es' ? '30+ anos de relaciones reales con el sistema legal de Houston.' : '30+ years of real relationships with the Houston legal system.' },
  ];

  return (
    <>
      <Helmet>
        <title>About Lone Star Bonding Co. | Steve Cruz | 30+ Years Houston Bail Bonds | License #74629</title>
        <meta name="description" content="Learn about Steve Cruz and Lone Star Bonding Co. - Houston's first Latino-owned bail bond company. Over 30 years serving Harris County. License #74629. Call (713) 228-8800." />
        <link rel="canonical" href="https://lonestarbonding.com/about" />
      </Helmet>
      <SharedNav />

      {/* Header */}
      <section style={{ paddingTop: '10rem', paddingBottom: '4rem', background: 'var(--void)', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: 800 }}>
          <Reveal>
            <span className="section-header__label">{lang === 'es' ? 'NUESTRA HISTORIA' : 'OUR STORY'}</span>
            <h1 className="section-header__title" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}>
              {lang === 'es' ? 'SOBRE LONE STAR BONDING CO.' : 'ABOUT LONE STAR BONDING CO.'}
            </h1>
            <p className="section-header__subtitle">
              {lang === 'es' ? 'La autoridad en fianzas de Houston desde 1994' : "Houston's bail bond authority since 1994"}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Steve Cruz Feature */}
      <section style={{ padding: '5rem 2rem', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'center' }}>
          <Reveal>
            <div style={{ padding: '2.5rem', background: 'var(--elevated)', border: '1px solid rgba(235, 110, 31, 0.15)', textAlign: 'center', position: 'relative' }}>
              <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--astros-orange), var(--texas-red))' }} />
              <div style={{ width: 120, height: 120, borderRadius: '50%', background: 'linear-gradient(135deg, var(--astros-orange), var(--texas-red))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 0 40px rgba(235, 110, 31, 0.3)' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'white' }}>SC</span>
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'white', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>STEVE CRUZ</h3>
              <p style={{ color: 'var(--astros-orange)', fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em', marginBottom: '1.5rem' }}>
                {lang === 'es' ? 'AGENTE DE FIANZAS LICENCIADO — LIC. NO. 74629' : 'LICENSED BAIL BONDSMAN — LICENSE NO. 74629'}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['30+ Years', 'Lic. #74629', 'Harris Co.', 'Montgomery Co.'].map(tag => (
                  <span key={tag} style={{ background: 'rgba(235, 110, 31, 0.08)', border: '1px solid rgba(235, 110, 31, 0.2)', padding: '0.2rem 0.6rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{tag}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: 'white', textTransform: 'uppercase', marginBottom: '1.5rem', lineHeight: 1.1 }}>
              {lang === 'es' ? 'MAS DE 30 ANOS SIRVIENDO A HOUSTON' : 'OVER 30 YEARS SERVING HOUSTON'}
            </h2>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
              {lang === 'es'
                ? 'Por mas de 30 anos, Steve Cruz y Lone Star Bonding Co. han servido a la comunidad de Houston con apoyo de fianzas practico y experimentado.'
                : 'For over 30 years, Steve Cruz and Lone Star Bonding Co. have served the Houston community with practical, experienced bail bond support.'}
            </p>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
              {lang === 'es'
                ? 'Como la primera empresa de fianzas latina de Houston, Steve Cruz se convirtio en una voz de confianza y lider en la comunidad local.'
                : "As Houston's first Latino-owned bail bond company, Steve Cruz became a trusted voice and leader in the local bonding and legal community through decades of real service."}
            </p>
            <Link to="/how-bail-works" className="btn-ghost" style={{ marginTop: '0.5rem' }}>
              {lang === 'es' ? 'COMO FUNCIONA' : 'HOW BAIL WORKS'} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: '5rem 2rem', background: 'var(--void)' }}>
        <div className="container">
          <Reveal><div className="section-header">
            <h2 className="section-header__title" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>{lang === 'es' ? 'NUESTROS VALORES' : 'OUR CORE VALUES'}</h2>
          </div></Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="authority-card" style={{ textAlign: 'center' }}>
                  <div className="authority-card__icon" style={{ margin: '0 auto 1rem' }}>{v.icon}</div>
                  <h3 className="authority-card__title">{v.title}</h3>
                  <p className="authority-card__text">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section style={{ padding: '5rem 2rem', background: 'var(--surface)' }}>
        <div className="container" style={{ maxWidth: 800 }}>
          <Reveal><div className="section-header">
            <h2 className="section-header__title" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>{lang === 'es' ? 'NUESTRA HISTORIA' : 'OUR HISTORY'}</h2>
          </div></Reveal>
          <div style={{ position: 'relative', paddingLeft: '3rem' }}>
            <div style={{ position: 'absolute', left: '0.85rem', top: 0, bottom: 0, width: 2, background: 'linear-gradient(180deg, var(--astros-orange) 0%, rgba(235, 110, 31, 0.1) 100%)' }} />
            {milestones.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{ position: 'relative', marginBottom: '2.5rem', paddingLeft: '1.5rem' }}>
                  <div style={{ position: 'absolute', left: '-2.15rem', top: 4, width: 18, height: 18, borderRadius: '50%', background: 'var(--astros-orange)', boxShadow: '0 0 10px rgba(235, 110, 31, 0.5)' }} />
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--astros-orange)', marginBottom: '0.3rem' }}>{m.year}</div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.6 }}>{lang === 'es' ? m.es : m.en}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '5rem 2rem', background: 'var(--void)', textAlign: 'center', borderTop: '1px solid var(--border-dim)' }}>
        <Reveal>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'white', textTransform: 'uppercase', marginBottom: '1rem' }}>
            {lang === 'es' ? 'LISTO PARA EMPEZAR?' : 'READY TO GET STARTED?'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', maxWidth: 550, margin: '0 auto 2rem' }}>
            {lang === 'es' ? 'Inicia tu proceso de fianza o llamenos directamente — disponibles 24/7.' : 'Start your bond process online or call us directly — available 24/7.'}
          </p>
          <a href="tel:7132288800" className="btn-emergency" data-testid="about-call-btn">
            <Phone size={20} /> (713) 228-8800
          </a>
        </Reveal>
      </section>
      <SharedFooter />
    </>
  );
};

export default AboutPage;
